import type { PatchRow } from '@/lib/diff/parse-patch-rows';
import type { PrV2GithubReviewSide } from '@/lib/pr-v2/types';
import type { PrV2FileDraftState } from '@/lib/stores/pr-v2-draft-store';

import { fenceText } from '@/components/chat/message-part-utils';
import { parsePatchRows } from '@/lib/diff/parse-patch-rows';
import { sanitizePromptContent } from '@/lib/security/prompt-sanitization';
import { asPositiveInteger } from '@/lib/stores/pr-v2-draft-store';

/** File data the draft's lines are read from. */
export type DraftCommentCodeSource = {
  /** GitHub's unified patch; preferred because it shows which lines were added or removed. */
  patch: string | null;
  oldContent: string | null;
  newContent: string | null;
};

type BuildDraftCommentAiChatTextParams = DraftCommentCodeSource & {
  filename: string;
  draft: Pick<PrV2FileDraftState, 'comment' | 'line' | 'side' | 'startLine' | 'startSide'>;
};

type DraftLineRange = {
  line: number;
  side: PrV2GithubReviewSide;
  startLine: number;
  startSide: PrV2GithubReviewSide;
};

// Long selections are cut so one question cannot flood the chat request.
const MAX_SNIPPET_LINES = 60;
// Paths and code come from the PR; keep code as written while neutralizing injection text.
const SOURCE_SANITIZE_OPTIONS = {
  normalizeHomoglyphs: 'injection-only',
  normalizeWhitespace: false,
  maxLength: Number.MAX_SAFE_INTEGER,
} as const;

function resolveDraftLineRange(
  draft: BuildDraftCommentAiChatTextParams['draft'],
): DraftLineRange | null {
  const line = asPositiveInteger(draft.line);
  if (!line) {
    return null;
  }

  const side = draft.side ?? 'RIGHT';
  const startLine = asPositiveInteger(draft.startLine);
  if (!startLine) {
    return { line, side, startLine: line, startSide: side };
  }

  return { line, side, startLine, startSide: draft.startSide ?? side };
}

function formatSide(side: PrV2GithubReviewSide): 'old' | 'new' {
  return side === 'LEFT' ? 'old' : 'new';
}

function formatLocation(filename: string, range: DraftLineRange | null): string {
  if (!range) {
    return `@${filename}`;
  }

  const { line, side, startLine, startSide } = range;
  if (startSide !== side) {
    // Split view can span a range from the old side to the new side.
    return `@${filename} · ${formatSide(startSide)} line ${startLine} to ${formatSide(side)} line ${line}`;
  }

  if (startLine === line) {
    return `@${filename} · ${formatSide(side)} line ${line}`;
  }

  return `@${filename} · ${formatSide(side)} lines ${Math.min(startLine, line)}-${Math.max(startLine, line)}`;
}

function findPatchRowIndex(rows: PatchRow[], side: PrV2GithubReviewSide, line: number): number {
  return rows.findIndex((row) => (side === 'LEFT' ? row.oldLine : row.newLine) === line);
}

/**
 * Diff rows from the draft's first line to its last, including removed lines in between.
 * Null when the patch misses either end or the range spans hunks, whose gap the patch omits.
 */
function readPatchSnippet(patch: string, range: DraftLineRange): string[] | null {
  const rows = parsePatchRows(patch);
  const startIndex = findPatchRowIndex(rows, range.startSide, range.startLine);
  const endIndex = findPatchRowIndex(rows, range.side, range.line);
  if (startIndex === -1 || endIndex === -1 || rows[startIndex]?.hunk !== rows[endIndex]?.hunk) {
    return null;
  }

  return rows
    .slice(Math.min(startIndex, endIndex), Math.max(startIndex, endIndex) + 1)
    .map((row) => row.text);
}

function readContentSnippet(
  { oldContent, newContent }: DraftCommentCodeSource,
  range: DraftLineRange,
): string[] | null {
  if (range.startSide !== range.side) {
    return null;
  }

  const content = range.side === 'LEFT' ? oldContent : newContent;
  if (content == null) {
    return null;
  }

  const lines = content.split('\n');
  const firstLine = Math.min(range.startLine, range.line);
  const lastLine = Math.max(range.startLine, range.line);
  if (lastLine > lines.length) {
    return null;
  }

  return lines.slice(firstLine - 1, lastLine);
}

function formatCodeBlock(lines: string[], language: string): string {
  const shownLines = lines.slice(0, MAX_SNIPPET_LINES);
  const block = fenceText(
    sanitizePromptContent(shownLines.join('\n'), SOURCE_SANITIZE_OPTIONS) ?? '',
    language,
  );

  return lines.length > shownLines.length
    ? `${block}\n(first ${shownLines.length} of ${lines.length} lines)`
    : block;
}

function formatCodeSnippet(source: DraftCommentCodeSource, range: DraftLineRange): string | null {
  const patchLines = source.patch ? readPatchSnippet(source.patch, range) : null;
  if (patchLines) {
    return formatCodeBlock(patchLines, 'diff');
  }

  const contentLines = readContentSnippet(source, range);
  return contentLines ? formatCodeBlock(contentLines, '') : null;
}

/**
 * Turns a draft review comment into a sidebar chat question: the question first, so the
 * chat bubble and the queue preview lead with it, then where it was written and the code.
 */
export function buildDraftCommentAiChatText({
  filename,
  draft,
  patch,
  oldContent,
  newContent,
}: BuildDraftCommentAiChatTextParams): string {
  // Collapse filename whitespace before sanitizing so it cannot fabricate extra header lines.
  const safeFilename =
    sanitizePromptContent(filename.replace(/\s+/g, ' ').trim(), SOURCE_SANITIZE_OPTIONS) ??
    'unknown file';
  const range = resolveDraftLineRange(draft);
  const codeSnippet = range ? formatCodeSnippet({ patch, oldContent, newContent }, range) : null;

  return [draft.comment.trim(), `Location: ${formatLocation(safeFilename, range)}`, codeSnippet]
    .filter(Boolean)
    .join('\n\n');
}
